import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faRotateLeft,
  faClock,
  faCheck,
  faBrain,
  faBan,
  faCircleCheck,
  faTriangleExclamation,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import flashcardService, { isQuotaError } from '../../services/flashcardService';
import UpgradePrompt from './UpgradePrompt';
import './Flashcards.css';

// UI button -> API response mapping (spec §4.1).
const RESPONSES = [
  { key: 'Again', label: 'Repeat <1m', cls: 'fc-resp-again', icon: faRotateLeft },
  { key: 'Hard', label: 'Hard <6m', cls: 'fc-resp-hard', icon: faClock },
  { key: 'Good', label: 'Fair <10m', cls: 'fc-resp-good', icon: faCheck },
  { key: 'Easy', label: 'Easy 4d', cls: 'fc-resp-easy', icon: faBrain },
  { key: 'Never', label: 'Never', cls: 'fc-resp-never', icon: faBan },
];

// Re-insert a requeued card roughly this many positions later (spec §7.2).
const REQUEUE_OFFSET = 3;

const StudySession = ({ deck, entitlement, onExit, onSwitchVersion }) => {
  const [phase, setPhase] = useState('loading'); // loading|studying|summary|empty|dailyCap|error
  const [sessionId, setSessionId] = useState(null);
  const [queue, setQueue] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [cardsStudied, setCardsStudied] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [studyRemainingToday, setStudyRemainingToday] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const cardStartRef = useRef(Date.now());
  const startedRef = useRef(false);

  const beginCardTimer = () => {
    cardStartRef.current = Date.now();
  };

  const finishSession = useCallback(async () => {
    try {
      const res = await flashcardService.endStudySession(sessionId);
      setSummary(res);
    } catch {
      // Fall back to locally tracked counts if the end call fails.
      setSummary(null);
    }
    setPhase('summary');
  }, [sessionId]);

  // Start the session once on mount.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let active = true;
    flashcardService
      .startStudySession(deck.id)
      .then((res) => {
        if (!active) return;
        setSessionId(res.sessionId);
        setStudyRemainingToday(
          res.studyRemainingToday === undefined ? null : res.studyRemainingToday
        );
        const cards = res.cards || [];
        if (cards.length === 0) {
          setPhase(res.studyRemainingToday === 0 ? 'dailyCap' : 'empty');
        } else {
          setQueue(cards);
          cardStartRef.current = Date.now();
          setPhase('studying');
        }
      })
      .catch(() => {
        if (active) setPhase('error');
      });
    return () => {
      active = false;
    };
  }, [deck.id]);

  const handleResponse = async (responseKey) => {
    if (submitting || queue.length === 0) return;
    const current = queue[0];
    const timeTakenMs = Date.now() - cardStartRef.current;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await flashcardService.submitResponse(sessionId, {
        cardId: current.cardId,
        response: responseKey,
        timeTakenMs,
      });

      const isCorrect = responseKey === 'Good' || responseKey === 'Easy';
      setCardsStudied((n) => n + 1);
      if (isCorrect) setCorrectCount((n) => n + 1);
      if (studyRemainingToday !== null) {
        setStudyRemainingToday((r) => (r > 0 ? r - 1 : 0));
      }

      const rest = queue.slice(1);
      if (result && result.requeue) {
        const pos = Math.min(REQUEUE_OFFSET, rest.length);
        rest.splice(pos, 0, current);
      }

      setFlipped(false);
      if (rest.length === 0) {
        setQueue([]);
        await finishSession();
      } else {
        setQueue(rest);
        beginCardTimer();
      }
    } catch (err) {
      if (isQuotaError(err)) {
        // Free cap hit mid-session: end gracefully, show summary under the prompt.
        try {
          const res = await flashcardService.endStudySession(sessionId);
          setSummary(res);
        } catch {
          setSummary(null);
        }
        setPhase('dailyCap');
      } else if (err && err.status === 404) {
        setPhase('error');
      } else {
        setSubmitError('Could not save that answer. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = async () => {
    if (phase === 'studying' && sessionId) {
      try {
        await flashcardService.endStudySession(sessionId);
      } catch {
        /* best-effort */
      }
    }
    onExit();
  };

  const current = queue[0];
  const total = cardsStudied + queue.length;

  // ----- Non-studying phases ------------------------------------------------
  const renderSummaryStats = () => {
    const studied = summary?.cardsStudied ?? cardsStudied;
    const correct = summary?.correctCount ?? correctCount;
    const score = studied > 0 ? Math.round((correct / studied) * 100) : 0;
    return (
      <div className="fc-summary-stats">
        <div className="fc-summary-stat">
          <span className="fc-summary-num">{studied}</span>
          <span className="fc-summary-lbl">Cards Studied</span>
        </div>
        <div className="fc-summary-stat">
          <span className="fc-summary-num">{correct}</span>
          <span className="fc-summary-lbl">Correct</span>
        </div>
        <div className="fc-summary-stat">
          <span className="fc-summary-num">{score}%</span>
          <span className="fc-summary-lbl">Score</span>
        </div>
      </div>
    );
  };

  return (
    <div className="fc-study">
      <div className="fc-study-header">
        <button type="button" className="fc-btn fc-btn-ghost" onClick={handleExit}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Decks
        </button>
        <div className="fc-study-titles">
          <h2>{deck.name}</h2>
          {phase === 'studying' && (
            <div className="fc-study-submeta">
              <span>
                Card {cardsStudied + 1} of {total}
              </span>
              <span>Correct: {correctCount}</span>
            </div>
          )}
        </div>
        {phase === 'studying' && studyRemainingToday !== null && (
          <span className="fc-cards-left-badge">
            {studyRemainingToday} cards left today
          </span>
        )}
      </div>

      {deck.isSnapshot && deck.hasNewerVersion && (
        <div className="fc-newer-version-banner">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>A newer version of this deck is available.</span>
          <button
            type="button"
            className="fc-btn fc-btn-ghost fc-btn-sm"
            onClick={onSwitchVersion}
          >
            <FontAwesomeIcon icon={faArrowsRotate} /> Switch to newest version
          </button>
        </div>
      )}

      {phase === 'loading' && (
        <div className="fc-state">
          <FontAwesomeIcon icon={faSpinner} className="fc-spin fc-state-icon" />
          <p>Building your study queue…</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="fc-state">
          <div className="fc-state-icon">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </div>
          <h3>Deck no longer available</h3>
          <p>We couldn't continue this session. The deck may have been removed.</p>
          <button type="button" className="fc-btn fc-btn-primary" onClick={onExit}>
            Back to Decks
          </button>
        </div>
      )}

      {phase === 'empty' && (
        <div className="fc-state">
          <div className="fc-state-icon">
            <FontAwesomeIcon icon={faCircleCheck} />
          </div>
          <h3>All caught up</h3>
          <p>No cards are due right now. Come back later to keep your streak going.</p>
          <button type="button" className="fc-btn fc-btn-primary" onClick={onExit}>
            Back to Decks
          </button>
        </div>
      )}

      {phase === 'dailyCap' && (
        <div className="fc-summary">
          {(summary || cardsStudied > 0) && renderSummaryStats()}
          <UpgradePrompt
            variant="dailyCap"
            requiredPlans={entitlement?.requiredPlans}
            onDismiss={onExit}
            dismissLabel="Back to Decks"
          />
        </div>
      )}

      {phase === 'summary' && (
        <div className="fc-summary">
          <h2>Session complete</h2>
          {renderSummaryStats()}
          <button type="button" className="fc-btn fc-btn-primary" onClick={onExit}>
            Back to Decks
          </button>
        </div>
      )}

      {phase === 'studying' && current && (
        <div className="fc-flip-wrap">
          {submitError && <div className="fc-error-banner">{submitError}</div>}
          <div className={`fc-flip-card ${flipped ? 'fc-flip-answer' : ''}`}>
            {!flipped ? (
              <>
                <span className="fc-flip-side-label">Question</span>
                <div className="fc-flip-content">{current.front}</div>
                <button
                  type="button"
                  className="fc-btn fc-btn-primary"
                  onClick={() => setFlipped(true)}
                >
                  <FontAwesomeIcon icon={faBrain} /> Show Answer
                </button>
              </>
            ) : (
              <>
                <span className="fc-flip-side-label">Answer</span>
                <div className="fc-flip-content">{current.back}</div>
                <hr className="fc-flip-divider" />
                <div className="fc-responses">
                  {RESPONSES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      className={`fc-resp ${r.cls}`}
                      disabled={submitting}
                      onClick={() => handleResponse(r.key)}
                    >
                      <FontAwesomeIcon icon={r.icon} /> {r.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudySession;
